/**
 * Paleta de las graficas del panel.
 *
 * Los colores no se eligen a ojo: salen del metodo de la skill `dataviz` y
 * estan pasados por su validador (`validate_palette.js`), que mide banda de
 * luminosidad OKLCH, suelo de croma, separacion bajo daltonismo y contraste
 * contra la superficie sobre la que se pinta de verdad.
 *
 *   node scripts/validate_palette.js "#2a78d6" --mode light --surface "#ffffff"
 *     [PASS] banda de luminosidad, croma, contraste -> TODO PASA
 *   node scripts/validate_palette.js "#3987e5" --mode dark  --surface "#0f172a"
 *     [PASS] banda de luminosidad, croma, contraste -> TODO PASA
 *
 * El modo oscuro NO es el claro invertido: es el mismo tono azul elegido con su
 * propio paso para la banda oscura (OKLCH L 0.48-0.67) y validado contra la
 * superficie oscura real. Invertir automaticamente saca al color de su banda y
 * lo deja o apagado o chillon.
 *
 * Los valores se publican como custom properties de CSS y no como constantes
 * de JavaScript porque asi el cambio claro/oscuro lo resuelve el navegador,
 * sin escuchar `matchMedia` ni volver a renderizar. Recharts los acepta tal
 * cual: `fill` y `stroke` son propiedades de presentacion y admiten `var()`.
 */

/** Superficies contra las que se ha validado el contraste. */
export const SUPERFICIE_CLARA = '#ffffff'
export const SUPERFICIE_OSCURA = '#0f172a'

/** Serie unica. Ranura categorica 1 (azul) en su paso de cada modo. */
export const SERIE_CLARA = '#2a78d6'
export const SERIE_OSCURA = '#3987e5'

/** Clase que ancla las variables; va en la raiz de cada grafica. */
export const CLASE_GRAFICA = 'viz-raiz'

/**
 * Roles, no colores. El componente se escribe contra estos nombres, de modo que
 * los dos temas se cambian en un unico sitio.
 */
export const TINTA = {
  superficie: 'var(--viz-superficie)',
  serie: 'var(--viz-serie)',
  primaria: 'var(--viz-tinta)',
  secundaria: 'var(--viz-tinta-2)',
  /** Ejes y etiquetas de eje: recesivos, pero por encima de 4.5:1. */
  apagada: 'var(--viz-tinta-apagada)',
  rejilla: 'var(--viz-rejilla)',
  eje: 'var(--viz-eje)',
  borde: 'var(--viz-borde)',
} as const

/**
 * Hoja local con los dos temas. Se inyecta con un `<style>` junto a la grafica,
 * como recomienda la skill: mantiene la paleta al lado de lo que pinta y no
 * obliga a tocar el CSS global de la aplicacion.
 *
 * Los valores oscuros se declaran dos veces: para un interruptor de tema
 * (`data-theme="oscuro"`) y para la preferencia del sistema, esta condicionada a
 * `data-theme="sistema"` en vez de aplicarse siempre.
 *
 * Esa condicion se puso cuando el armazon era solo claro, para que un usuario
 * con el sistema en oscuro no viera una sola tarjeta negra sobre una pagina
 * clara. El armazon ya declara el tema (`src/tema/`, tokens en `index.css`), asi
 * que la paleta oscura se enciende sola, que es lo que este comentario
 * anticipaba. La condicion se mantiene porque sigue siendo la correcta: es el
 * mismo atributo el que manda en la pagina y en la grafica, y por eso no pueden
 * discrepar.
 */
const CLARO = `
    color-scheme: light;
    --viz-superficie: ${SUPERFICIE_CLARA};
    --viz-serie: ${SERIE_CLARA};
    --viz-tinta: #0f172a;
    --viz-tinta-2: #475569;
    --viz-tinta-apagada: #64748b;
    --viz-rejilla: #e2e8f0;
    --viz-eje: #cbd5e1;
    --viz-borde: rgba(15, 23, 42, 0.1);`

const OSCURO = `
    color-scheme: dark;
    --viz-superficie: ${SUPERFICIE_OSCURA};
    --viz-serie: ${SERIE_OSCURA};
    --viz-tinta: #f8fafc;
    --viz-tinta-2: #cbd5e1;
    --viz-tinta-apagada: #94a3b8;
    --viz-rejilla: #1e293b;
    --viz-eje: #334155;
    --viz-borde: rgba(248, 250, 252, 0.12);`

export const CSS_PALETA = `
.${CLASE_GRAFICA} {${CLARO}
}
@media (prefers-color-scheme: dark) {
  :root[data-theme="sistema"] .${CLASE_GRAFICA} {${OSCURO}
  }
}
:root[data-theme="oscuro"] .${CLASE_GRAFICA} {${OSCURO}
}
`
